import { DateRange, CheqLoader } from '@clickcease/cc-client-components-lib'
import { getUnixTime } from 'date-fns'
import React, { useEffect, useRef } from 'react'

import { CountriesBreakdown } from '../components/FederatedContentComponents/CountriesBreakdown/CountriesBreakdown'
import { Header } from '../components/FederatedContentComponents/Header/Header'
import { HeatMap } from '../components/FederatedContentComponents/HeatMap/HeatMap'
import { InsightsWidget } from '../components/FederatedContentComponents/InsightsWidget/InsightsWidget'
import { PDFHeader } from '../components/FederatedContentComponents/PDFHeader/PDFHeader'
import { SummaryWidget } from '../components/FederatedContentComponents/SummaryWidget/SummaryWidget'
import { ThreatTypesAndGroupsWidget } from '../components/FederatedContentComponents/ThreatTypesAndGroupsWidget/ThreatTypesAndGroupsWidget'

import { rangeOptions } from '../../../components/DateRangeSelect/utilities'
import { useBMStore } from '../../../stores/useBMStore'
import { ProductType } from '../../../types/federatedContext'
import { DomainsTable } from '../components/FederatedContentComponents/DomainsTable/DomainsTable'

import {
  DashboardWrapper,
  LoaderWrapper,
  CountriesSection,
  BMDashboardStyled,
  BMDashboardPDF,
} from './NewDashboard.styled'
import { useSummaryQuery } from '../../../hooks/api/useSummaryQuery'

interface NewDashboardUIProps {
  locale?: string
  accountLogoDownloadLink?: string | undefined
  userPlans?: ProductType[]
}

export const NewDashboardUI: React.FC<NewDashboardUIProps> = ({
  locale,
  accountLogoDownloadLink,
  userPlans,
}) => {
  const domains = useBMStore((state) => state.domains)
  const chosenDomains = useBMStore((state) => state.chosenDomains)
  const setDateRange = useBMStore((state) => state.setDateRange)
  const storeRange = useBMStore((state) => state.dateRange)
  const { isLoading, summary } = useSummaryQuery()

  useEffect(() => {
    if (!storeRange.fromDate || !storeRange.toDate) {
      const defaultRange = rangeOptions.WEEK.range as DateRange
      setDateRange(defaultRange.from as Date, defaultRange.to as Date)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const localDateRange =
    storeRange.fromDate && storeRange.toDate
      ? {
          from: getUnixTime(new Date(storeRange.fromDate)),
          to: getUnixTime(new Date(storeRange.toDate)),
        }
      : undefined

  const onDateRangeSelected = (newDateRange: DateRange | undefined) => {
    if (newDateRange) {
      setDateRange(newDateRange.from as Date, newDateRange.to as Date)
    }
  }

  const printRef = useRef(null)

  return (
    <DashboardWrapper>
      {isLoading ? (
        <LoaderWrapper>
          <CheqLoader />
        </LoaderWrapper>
      ) : (
        domains &&
        domains.length > 0 && (
          <>
            <BMDashboardStyled>
              <Header
                locale={locale}
                dateRange={localDateRange}
                onDateRangeSelected={onDateRangeSelected}
                printRef={printRef}
              />
              {domains.length > 1 && (!chosenDomains || chosenDomains.length > 1) && (
                <DomainsTable summary={summary} />
              )}
              <SummaryWidget summary={summary} />
              <InsightsWidget summary={summary} />
              <ThreatTypesAndGroupsWidget summary={summary} />
              <CountriesSection>
                <CountriesBreakdown summary={summary} />
                <HeatMap summary={summary} />
              </CountriesSection>
            </BMDashboardStyled>
            <BMDashboardPDF ref={printRef}>
              <PDFHeader
                locale={locale}
                userPlans={userPlans}
                accountLogoDownloadLink={accountLogoDownloadLink}
              />
              <SummaryWidget summary={summary} />
              <InsightsWidget isPdf summary={summary} />
              <ThreatTypesAndGroupsWidget isPdf summary={summary} />
              <CountriesSection>
                <CountriesBreakdown summary={summary} />
                <HeatMap summary={summary} />
              </CountriesSection>
            </BMDashboardPDF>
          </>
        )
      )}
    </DashboardWrapper>
  )
}
